import { useContext } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  PersonCountryFilterSetting,
} from "../../../../interfaces/filters";
import { PersonDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionSubFilterItem } from "../../../accordion/Accordion";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import useCountries from "../../../../hooks/useCountries";
import SearchIcon from "../../../icons/SearchIcon";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "country";

export default function PersonLandeFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // // find active settings
  const settings: PersonCountryFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const { countries } = useCountries();

  // handlers
  function updateFilterHandler(selectedItem: { kode: string; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.kode === selectedItem.kode)) {
      settings.push({ kode: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("adresse", {
        or: settings.map((set) => {
          return {
            adresse: {
              landekode: {
                eq: set.kode,
              },
            },
          };
        }),
      })
      .build();

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonCountryFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: PersonCountryFilterSetting["kode"]) {
    const updatedSettings: PersonCountryFilterSetting[] =
      settings.filter((set) => {
        return id !== set.kode;
      }) || [];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("adresse", {
        or: updatedSettings.map((set) => {
          return {
            adresse: {
              landekode: {
                eq: set.kode,
              },
            },
          };
        }),
      })
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Land"
      value="land"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.kode}
              label={filter.name}
              labelPrefix={filter.kode}
              removeFilterHandler={() => removeFilterHandler(filter.kode)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={countries}
        itemsIdKey="Id"
        itemToString={(item) => item?.Navn ?? ""}
        itemToElement={(item) => (
          <DefaultDropdownItem
            label={`${item.Navn}`}
            subLabel={`Landekode: ${item.Landekode}`}
          />
        )}
        filterKeys={["Navn", "Landekode"]}
        placeholder="Søg efter navn eller landekode ..."
        closedIcon={<SearchIcon height={14} />}
        openIcon={<SearchIcon height={14} />}
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.Landekode && item.selectedItem.Navn) {
            updateFilterHandler({
              kode: item.selectedItem.Landekode,
              name: item.selectedItem.Navn,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
